import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtLog, fmtLogDay } from "@/lib/tz";
import { saveBlob } from "@/lib/govDocs";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Download, LifeBuoy, Loader2 } from "lucide-react";

const CRIMSON = "var(--brand)";
const HOURS = [1, 4, 8];
const MODES = [
  { key: "look", label: "Look only", hint: "See everything, change nothing" },
  { key: "fix", label: "Look and fix", hint: "As an admin, except logins, roles, security settings, support access and exports" },
];
const hoursLabel = (h) => `${h} hour${h === 1 ? "" : "s"}`;
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const happened = (c) =>
  `${plural(c.opened, "record opened", "records opened")}, ${plural(c.changes, "change", "changes")}, ${plural(c.downloads, "download", "downloads")}`;

function Choice({ on, onClick, children, testid }) {
  return (
    <button type="button" aria-pressed={on} onClick={onClick} data-testid={testid}
      className={`min-h-[44px] rounded-lg px-4 py-2.5 text-left text-sm font-semibold ${on ? "border-2 bg-rose-50" : "border border-slate-400 bg-white"}`}
      style={on ? { borderColor: CRIMSON } : undefined}>
      {children}
    </button>
  );
}

// One past session's record: every record opened, every change (before and after), downloads, sign-in and out.
function SessionRecord({ id, onClose }) {
  const [rec, setRec] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.supportSessionRecord(id).then(setRec).catch(() => toast.error("Couldn't load the record")); }, [id]);
  const download = async () => {
    setSaving(true);
    try {
      saveBlob(await api.supportSessionPdf(id), `Support session ${fmtLogDay(rec.session.started_at)}.pdf`);
    } catch {
      toast.error("Couldn't make the PDF");
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Support session record</DialogTitle></DialogHeader>
        {!rec ? <Loader2 className="h-5 w-5 animate-spin" /> : (
          <div className="space-y-5 text-sm" data-testid="support-record">
            <p>{rec.session.tech} ({rec.session.mode_label}), {fmtLog(rec.session.started_at)} to {fmtLog(rec.session.ended_at)}.
              Given by {rec.session.given_by}. Reason: {rec.session.reason || "none given"}. Ended: {rec.session.ended_reason}.</p>
            <Button variant="outline" onClick={download} disabled={saving} data-testid="support-record-pdf">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}Download PDF
            </Button>
            <section>
              <h3 className="font-semibold mb-1">Changes ({rec.changes.length})</h3>
              {rec.changes.length === 0 ? <p className="text-slate-500">None.</p> : (
                <ul className="space-y-2">
                  {rec.changes.map((c) => (
                    <li key={c.id} className="rounded border border-slate-200 p-2">
                      <div className="font-medium">{fmtLog(c.timestamp)}: {c.action} {c.doctype} {c.docname}</div>
                      {(c.changes || []).map((ch, i) => (
                        <div key={i} className="text-slate-600">{ch.field}: {String(ch.before ?? "")} → {String(ch.after ?? "")}</div>
                      ))}
                    </li>
                  ))}
                </ul>
              )}
            </section>
            <section>
              <h3 className="font-semibold mb-1">Records opened and downloads ({rec.opened.length})</h3>
              {rec.opened.length === 0 ? <p className="text-slate-500">None.</p> : (
                <ul className="space-y-1">
                  {rec.opened.map((o) => (
                    <li key={o.id}>{fmtLog(o.ts)}: {o.kind} {o.resource_type}{o.resource_id ? ` ${o.resource_id}` : ""}</li>
                  ))}
                </ul>
              )}
            </section>
            <section>
              <h3 className="font-semibold mb-1">Sign-in and sign-out</h3>
              <ul className="space-y-1">
                {rec.security.map((e) => <li key={e.id}>{fmtLog(e.ts)}: {e.action.replaceAll("_", " ")} ({e.outcome})</li>)}
              </ul>
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Admin Settings > Support access: Fully Funded Shifts can't see or change anything here unless an admin gives a code.
export default function SupportAccessSettings() {
  const { refreshUser } = useAuth();
  const [data, setData] = useState(null);
  const [hours, setHours] = useState(4);
  const [mode, setMode] = useState("fix");
  const [code, setCode] = useState(null);
  const [busy, setBusy] = useState(false);
  const [viewing, setViewing] = useState(null);

  const load = () => api.supportAccess().then(setData).catch(() => toast.error("Couldn't load support access"));
  useEffect(() => { load(); }, []);

  const act = async (fn, done) => {
    setBusy(true);
    try {
      const r = await fn();
      done?.(r);
      await load();
      refreshUser();
    } catch (e) {
      toast.error(e.response?.data?.detail || "That didn't work");
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <div className="p-6"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  const a = data.active;
  return (
    <div className="p-6 max-w-5xl space-y-5" data-testid="support-access-page">
      <div>
        <h1 className="font-display text-3xl text-slate-800 flex items-center gap-2"><LifeBuoy className="h-7 w-7" style={{ color: CRIMSON }} />Support access</h1>
        <p className="mt-1 text-slate-600 max-w-3xl">Fully Funded Shifts can't see or change anything in your app unless an admin here
          gives access. A code lets one support person in, once, for the time you choose.</p>
      </div>

      {a && (
        <section className="rounded-xl border border-slate-200 bg-white p-5 space-y-3 max-w-3xl" data-testid="support-active">
          <h2 className="font-display text-xl">Session in progress</h2>
          <p>{a.tech} from FFS support is signed in ({a.mode_label}, until {fmtLog(a.ends_at)}). Reason: {a.reason || "none given"}.
            Every admin was emailed when it started. Each record they open and each change they make is recorded under
            "{a.tech} (FFS support)".</p>
          <Button onClick={() => act(api.endSupportSession, () => toast.success("Support session ended"))} disabled={busy}
            style={{ backgroundColor: CRIMSON }} data-testid="support-end">End now</Button>
        </section>
      )}

      {code && (
        <section className="rounded-xl border-2 bg-white p-5 space-y-3 max-w-3xl" style={{ borderColor: CRIMSON }} data-testid="support-code">
          <h2 className="font-display text-xl">Read this code to the FFS support person</h2>
          <div className="font-display text-5xl tracking-widest text-slate-800" data-testid="support-code-value">{code.code}</div>
          <p>Works once, within 15 minutes, for {hoursLabel(code.hours)}, {MODES.find((m) => m.key === code.mode).label}.
            It is shown only now; we keep only a scrambled copy.</p>
          <Button variant="outline" disabled={busy} onClick={() => act(api.cancelSupportCode, () => setCode(null))}>Cancel the code</Button>
        </section>
      )}

      {!a && !code && (
        <section className="rounded-xl border border-slate-200 bg-white p-5 space-y-4 max-w-3xl">
          <h2 className="font-display text-xl">Give support access</h2>
          {data.locked_until && <p role="alert" className="rounded bg-amber-50 p-2 text-amber-900">Locked after 3 wrong codes until {fmtLog(data.locked_until)}.</p>}
          {data.code_waiting && <p className="text-slate-600">A code you gave earlier ({hoursLabel(data.code_waiting.hours)}, {data.code_waiting.mode_label})
            works until {fmtLog(data.code_waiting.expires_at)}. A new code cancels it.</p>}
          <div>
            <div className="text-sm font-semibold mb-2">How long</div>
            <div className="flex flex-wrap gap-2">
              {HOURS.map((h) => <Choice key={h} on={h === hours} onClick={() => setHours(h)} testid={`support-hours-${h}`}>{hoursLabel(h)}{h === 4 ? " (default)" : ""}</Choice>)}
            </div>
          </div>
          <div>
            <div className="text-sm font-semibold mb-2">What they may do</div>
            <div className="grid gap-2 sm:grid-cols-2">
              {MODES.map((m) => (
                <Choice key={m.key} on={m.key === mode} onClick={() => setMode(m.key)} testid={`support-mode-${m.key}`}>
                  <div>{m.label}</div><div className="text-xs font-normal text-slate-600">{m.hint}</div>
                </Choice>
              ))}
            </div>
          </div>
          <Button disabled={busy || !!data.locked_until} style={{ backgroundColor: CRIMSON }} data-testid="support-give"
            onClick={() => act(() => api.giveSupportAccess(hours, mode), setCode)}>Give support access</Button>
        </section>
      )}

      <section className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
        <h2 className="font-display text-xl px-5 pt-4 pb-1">Past sessions</h2>
        {data.past.length === 0 ? <p className="px-5 pb-4 text-slate-500 text-sm">None yet.</p> : (
          <table className="w-full min-w-[720px] text-sm">
            <thead><tr className="text-left text-slate-600 border-b">
              {["When", "Given by", "Support person", "Access", "What happened"].map((h) => <th key={h} className="px-4 py-2 font-semibold">{h}</th>)}
            </tr></thead>
            <tbody>
              {data.past.map((p) => (
                <tr key={p.id} className="border-b last:border-0 align-top">
                  <td className="px-4 py-2">{fmtLog(p.started_at)} to {fmtLog(p.ended_at)}</td>
                  <td className="px-4 py-2">{p.given_by}</td>
                  <td className="px-4 py-2">{p.tech} (FFS)</td>
                  <td className="px-4 py-2">{p.mode_label}, {hoursLabel(p.hours)}</td>
                  <td className="px-4 py-2">{happened(p.counts)} · <button type="button" className="underline" style={{ color: CRIMSON }} onClick={() => setViewing(p.id)}>Record</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
      {viewing && <SessionRecord id={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}
